
import { useEffect } from 'react';
import UpdatedHeroSection from './components/UpdatedHeroSection';
import HowItWorksSection from './components/HowItWorksSection';
import TrustElementsSection from './components/TrustElementsSection';
import FAQSection from './components/FAQSection';
import CalendarBookingSection from './components/CalendarBookingSection';
import Footer from './components/Footer';
import FloorplanSection from './components/FloorplanSection';
import GiadaSection from './components/GiadaSection';
import QuickMenu from '../../components/feature/QuickMenu';

export default function HomePage() {
  useEffect(() => {
    // Stable cache system - no reload loops
    const currentVersion = 'v180-stable';
    const lastVersion = localStorage.getItem('site_version');
    
    if (lastVersion !== currentVersion) {
      localStorage.setItem('site_version', currentVersion);
    }
    
    // Enhanced SEO
    document.title = 'Georgia Dealer License Consultation - Expert Guidance & Floorplan Financing';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Get expert consultation for your Georgia dealer license. Professional guidance, floorplan financing education, GIADA pre-license seminar info, and comprehensive course materials.');
    }

    // Add structured data for SEO (only once)
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (!existingScript) {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": "Premier Capital Holdings",
        "description": "Georgia Dealer License Consultation and Education",
        "url": "https://www.georgiadealerlicense.com",
        "offers": {
          "@type": "Offer",
          "name": "Georgia Dealer License Consultation",
          "description": "Expert consultation services for obtaining Georgia dealer license",
          "price": "97",
          "priceCurrency": "USD"
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Initialize analytics if available
    if (typeof gtag !== 'undefined') {
      gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: 'Georgia Dealer License Homepage',
        page_location: window.location.href
      });
    }

    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Quick Menu - Mobile Only (Sticky Bottom) */}
      <div className="lg:hidden">
        <QuickMenu isMobile={true} />
      </div>

      {/* Hero Section with Overview ID - Contains built-in navigation */}
      <div id="overview" style={{ scrollMarginTop: '80px' }}>
        <UpdatedHeroSection />
      </div>
      
      {/* Resources Section - NEW: Replaces What's Included */}
      <div id="resources" style={{ scrollMarginTop: '80px' }}>
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-folder-open-line text-2xl text-blue-600"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Complete Resource Library
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Everything you need to successfully obtain your Georgia used car dealer license - templates, guides, forms, and expert resources
                </p>
              </div>

              {/* Resource Categories Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-list-line text-xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Forms & Applications</h3>
                  <p className="text-gray-600 mb-4">Complete dealer license application checklist, pre-license seminar guide, and fingerprinting process documentation</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 47-point application checklist</li>
                    <li>• Pre-license seminar registration</li>
                    <li>• Background check requirements</li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-building-line text-xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Business Setup</h3>
                  <p className="text-gray-600 mb-4">Professional business plan templates, startup cost calculators, and incorporation guidance</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Business plan templates</li>
                    <li>• Financial projections</li>
                    <li>• Incorporation services</li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-scales-line text-xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Documents</h3>
                  <p className="text-gray-600 mb-4">Georgia-compliant purchase agreements, warranty forms, and consumer protection notices</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Purchase agreement templates</li>
                    <li>• Warranty disclosure forms</li>
                    <li>• Advertising compliance guide</li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-bank-line text-xl text-yellow-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Financing Guides</h3>
                  <p className="text-gray-600 mb-4">Floorplan financing education, lender network building, and credit application templates</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Floorplan financing guide</li>
                    <li>• Lender contact database</li>
                    <li>• Credit application forms</li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-megaphone-line text-xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Marketing Materials</h3>
                  <p className="text-gray-600 mb-4">Grand opening kits, social media templates, and email marketing campaigns</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 50+ social media templates</li>
                    <li>• Email marketing sequences</li>
                    <li>• Print marketing materials</li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-tools-line text-xl text-indigo-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Tools & Software</h3>
                  <p className="text-gray-600 mb-4">Inventory management spreadsheets, customer database templates, and industry contacts</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Inventory tracking tools</li>
                    <li>• Customer database template</li>
                    <li>• 100+ industry contacts</li>
                  </ul>
                </div>
              </div>

              {/* Value Proposition */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-4">$497+ Value in Professional Resources</h3>
                <p className="text-blue-100 text-lg mb-6">
                  Get instant access to all templates, guides, and resources with our complete course
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <a 
                    href="https://drive.google.com/file/d/15mwTL1E347Xo6zRAAb_miL0HbDsxiOHn/view?usp=sharing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-blue-600 px-8 py-3 rounded-full font-bold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Browse All Resources
                  </a>
                  <button
                    onClick={() => window.open('https://www.georgiadealerlicense.com/purchase', '_blank')}
                    className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Complete Access - $97
                  </button>
                </div>
              </div>

            </div>
          </div>
        </section>
      </div>

      {/* Floorplan Financing Section */}
      <div id="floorplan" style={{ scrollMarginTop: '80px' }}>
        <FloorplanSection />
      </div>
      
      {/* GIADA Pre-License Section */}
      <div id="giada" style={{ scrollMarginTop: '80px' }}>
        <GiadaSection />
      </div>
      
      {/* Pricing Section */}
      <div id="pricing" style={{ scrollMarginTop: '80px' }}>
        <TrustElementsSection />
      </div>

      {/* Class Schedule Section */}
      <div id="class-schedule" style={{ scrollMarginTop: '80px' }}>
        <CalendarBookingSection />
      </div>
      
      {/* FAQ Section */}
      <div id="faq" style={{ scrollMarginTop: '80px' }}>
        <FAQSection />
      </div>

      {/* Contact & Enroll Section */}
      <div id="contact" style={{ scrollMarginTop: '80px' }}>
        <div id="enroll">
          <Footer />
        </div>
      </div>
    </div>
  );
}
